import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Users, MessageSquare, Target, Megaphone, Send, CheckCheck, EyeOff, Flame } from "lucide-react";

const COLORS = { HOT: "#DC2626", HIGH: "#EA580C", MEDIUM: "#D97706", LOW: "#64748B" };

function Metric({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">{label}</div>
          <div className="text-2xl font-bold tracking-tight mt-1 text-slate-900">{value ?? 0}</div>
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [m, setM] = useState({});
  const [ch, setCh] = useState({ lead_funnel: [], lead_scores: [], campaigns: [] });

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => {});
    api.get("/dashboard/charts").then((r) => setCh(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Overview of your WhatsApp AI sales pipeline</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric icon={Users} label="Contacts" value={m.contacts} tint="bg-emerald-50 text-emerald-700" testid="metric-contacts" />
        <Metric icon={Target} label="Total Leads" value={m.leads} tint="bg-indigo-50 text-indigo-700" testid="metric-leads" />
        <Metric icon={Flame} label="Hot Leads" value={m.hot_leads} tint="bg-rose-50 text-rose-700" testid="metric-hot-leads" />
        <Metric icon={CheckCheck} label="Qualified" value={m.qualified_leads} tint="bg-amber-50 text-amber-700" testid="metric-qualified" />
        <Metric icon={MessageSquare} label="Conversations" value={m.conversations} tint="bg-emerald-50 text-emerald-700" testid="metric-conversations" />
        <Metric icon={MessageSquare} label="AI Active" value={m.ai_conversations} tint="bg-indigo-50 text-indigo-700" testid="metric-ai-active" />
        <Metric icon={Megaphone} label="Campaigns" value={m.campaigns} tint="bg-amber-50 text-amber-700" testid="metric-campaigns" />
        <Metric icon={Send} label="Msgs Sent" value={m.messages?.sent} tint="bg-slate-100 text-slate-700" testid="metric-sent" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          ["Delivered", m.messages?.delivered, "bg-emerald-500"],
          ["Read", m.messages?.read, "bg-indigo-500"],
          ["Failed", m.messages?.failed, "bg-rose-500"],
          ["Replies", m.messages?.replies, "bg-amber-500"],
        ].map(([l, v, c]) => (
          <Card key={l} className="p-4 border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-10 rounded-full ${c}`} />
              <div>
                <div className="text-xs text-slate-500 uppercase font-medium">{l}</div>
                <div className="text-xl font-bold">{v ?? 0}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200">
          <div className="mb-3">
            <div className="font-semibold text-slate-900">Lead Funnel</div>
            <div className="text-xs text-slate-500">Pipeline stage distribution</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ch.lead_funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#059669" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="mb-3">
            <div className="font-semibold text-slate-900">Lead Scores</div>
            <div className="text-xs text-slate-500">AI qualification quality</div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={ch.lead_scores} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={3}>
                {ch.lead_scores.map((e) => <Cell key={e.name} fill={COLORS[e.name] || "#64748B"} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 border-slate-200">
        <div className="mb-3">
          <div className="font-semibold text-slate-900">Recent Campaigns</div>
          <div className="text-xs text-slate-500">Sent • Read • Replies</div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={ch.campaigns}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip /><Legend />
            <Bar dataKey="sent" fill="#059669" />
            <Bar dataKey="read" fill="#6366F1" />
            <Bar dataKey="replies" fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
